import { Component, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AppComponent } from 'src/app/app.component';
import { AppState } from 'src/app/store/app.reducer';
import { featuresConfiguration } from 'src/app/conf/features-data';
import { HomeComponent } from '../home.component';
import { Lang } from 'src/app/model/lang';
import { selectCurrentLang } from 'src/app/store/user/user.selector';
import { DEFAULT_LANG, DEFAULT_SUPPORT_LANG_DOC_PAGE, LANGUAGES, LIST_SUPPORT_LANG_DOC_PAGE, SPARE_PARTS_PORTAL_LINK } from 'src/app/conf/languages';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import packageJson from 'package.json';

@Component({
	selector: 'app-user-menu',
	template: `
		<div id="logolink" class="layout-menu-logo" style="cursor: pointer; outline: none;" routerLink="/home/sites">
			<img class="mb-2" id="app-logo" [src]="getMenuTheme() === 'dim' ? './assets/images/logos/cameconnect-main.png' : './assets/images/logos/cameconnect-whiteblack.png'" alt="Came Connect logo">
		</div>
		<ul class="layout-menu">
			<li app-user-menuitem *ngFor="let item of model; let i = index;" [item]="item" [index]="i" [root]="true"></li>
		</ul>
		<div class="fixed bottom-0 flex align-items-end justify-content-between w-full mb-3">
			<div class="flex font-normal text-sm font-medium pl-3" [class]="getMenuTheme() === 'dim' ? 'text-300' : 'text-900'">R: {{ this.version }}</div>
			<div class="flex pr-3"><img [src]="getMenuTheme() === 'dim' ? 'assets/images/logos/CAME-A_alpha_white_blue.png' : 'assets/images/logos/CAME-A_alpha_white.png'" alt="Logo" style="height:1.5rem;"/></div>
		</div>
  `,
	styles:
		`
		img {
			-webkit-user-drag: none;
			user-drag: none;
		}
	`
})
export class AppUserMenuComponent implements OnInit {
	/**
	 * Local reference to the features configuration (for the html file)
	 */
	featsConfigRef = featuresConfiguration;
	/**
	 * Feature list observable referencing to the device store
	 */
	featuresSel$: Observable<string[]> = new Observable<string[]>();
	featuresSub: Subscription | undefined;

	model!: any[];

	// Languages & Dialog Language
	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;
	languages = LANGUAGES;
	ref: DynamicDialogRef | undefined;

	version: string;

	constructor(
		public appMain: HomeComponent,
		public app: AppComponent,
		private translate: TranslateService,
		private store: Store<AppState>
	) {
	}

	ngOnInit() {
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.translate.onLangChange.subscribe((langChangeEvent: LangChangeEvent) => {
			this.generateMenu(langChangeEvent.lang);
		});
		this.currentLang$.subscribe((lang: Lang) => {
			this.currentLang = lang;
			this.generateMenu(lang.code);
		});

		this.version = packageJson.version;
	}

	getMenuTheme(): string {
		return this.app.menuTheme;
	}

	generateMenu(lang: string) {
		let langUrlDocPage = DEFAULT_SUPPORT_LANG_DOC_PAGE;
		if (LIST_SUPPORT_LANG_DOC_PAGE.includes(lang.substring(0, 2))) {
			langUrlDocPage = lang.substring(0, 2);
		}
		let linkSparePartsPortal = SPARE_PARTS_PORTAL_LINK + 'en';
		switch (lang) {
			case 'it-IT': {
				linkSparePartsPortal = SPARE_PARTS_PORTAL_LINK + 'it';
				break;
			}
			case 'es-ES': {
				linkSparePartsPortal = SPARE_PARTS_PORTAL_LINK + 'es';
				break;
			}
			case 'de-DE': {
				linkSparePartsPortal = SPARE_PARTS_PORTAL_LINK + 'de';
				break;
			}
			case 'pl-PL': {
				linkSparePartsPortal = SPARE_PARTS_PORTAL_LINK + 'pl';
				break;
			}
			case 'nl-NL': {
				linkSparePartsPortal = SPARE_PARTS_PORTAL_LINK + 'nl';
				break;
			}
			case 'fr-FR': {
				linkSparePartsPortal = SPARE_PARTS_PORTAL_LINK + 'fr';
				break;
			}
			case 'ru-RU': {
				linkSparePartsPortal = SPARE_PARTS_PORTAL_LINK + 'ru';
				break;
			}
			case 'sk-SK': {
				linkSparePartsPortal = SPARE_PARTS_PORTAL_LINK + 'sk';
				break;
			}
			default: {
				linkSparePartsPortal = SPARE_PARTS_PORTAL_LINK + 'en';
				break;
			}
		}
		this.model = [
			{
				label: ' ',
				items: [
					{ label: this.translate.instant('SITES_MANAGEMENT'), icon: 'pi text-3xl cm-installation', routerLink: ['/home/sites'] },
					{ label: this.translate.instant('DEVICES'), icon: 'pi text-3xl cm-devices', routerLink: ['/home/devices'] },
					{ label: this.translate.instant('SCENARIOS'), icon: 'pi text-3xl cm-scenary', routerLink: ['/home/scenarios'] },
					{ label: this.translate.instant('PERMISSIONS'), icon: 'pi text-3xl cm-quick-easy', routerLink: ['/home/permissions'] },
					{ label: this.translate.instant('SUPPORT_REQUESTS'), icon: 'pi text-3xl cm-support-request', routerLink: ['/home/support'] },
					{ label: this.translate.instant('EVENTS'), icon: 'pi text-3xl cm-events', routerLink: ['/home/events'] },
					{ label: this.translate.instant('HELPER'), icon: 'pi text-3xl cm-help', routerLink: ['/home/helper'] },
					{
						label: this.translate.instant('TOOLS'), icon: 'pi text-3xl cm-toolbox', items: [
							{ label: this.translate.instant('TECHNICAL_DOCUMENTS_BUTTON'), icon: 'pi text-3xl cm-manual', url: ['https://docs.came.com/#/' + langUrlDocPage], target: '_blank', },
							{ label: this.translate.instant('SPARE_PARTS_PORTAL'), icon: 'pi text-3xl cm-spare-parts-portal', url: [linkSparePartsPortal], target: '_blank', },
							//{label: this.translate.instant('COMPLIANT_DEVICES'), icon: 'pi text-3xl cm-devices', url: ['https://download.cameconnect.net/manual/compliant_devices.pdf'], target: '_blank',},
							{ label: this.translate.instant('COMPLIANT_DEVICES'), icon: 'pi text-3xl cm-devices', routerLink: ['/home/supportDevice'] },
						]
					},
				]
			}
		];
	}
}
